import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";

// küçük bir debounce yardımcı fonksiyonu
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Accounts() {
  const history = useHistory();
  const location = useLocation();

  const [accountdata, setAccountData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 200);

  // Sekme durumu route'tan okunuyor; local state yerine url’e güven
  const tabs = [
    { key: "accounts", label: "Account", path: "/accounts" },
    { key: "guidelines", label: "Guidelines", path: "/guidelines" },
    { key: "yourteam", label: "Your Team", path: "/yourteam" },
  ];
  const activeKey =
    tabs.find((t) => location.pathname.startsWith(`/${t.key}`))?.key ||
    "accounts";

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    axios
      .get("https://act-api.vercel.app/accounts")
      .then((res) => {
        if (!mounted) return;
        setAccountData(Array.isArray(res.data) ? res.data : []);
        setErr(null);
      })
      .catch((e) => {
        if (!mounted) return;
        setErr("Veriler getirilirken bir sorun oluştu.");
      })
      .finally(() => mounted && setIsLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // filtre + sıralama
  const filteredData = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const base = accountdata || [];
    const filtered = term
      ? base.filter((i) => (i?.name || "").toLowerCase().includes(term))
      : base;
    // adı alfabetik sıralayalım
    return filtered.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [accountdata, debouncedSearch]);

  function go(path) {
    history.push(path);
  }
  function openAccount(id) {
    history.push("/accountupdate/" + id);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst Bar */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between py-3 gap-3">
            {/* Sekmeler */}
            <div className="w-full sm:w-auto">
              <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
                {tabs.map((t) => {
                  const active = activeKey === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => go(t.path)}
                      className={[
                        "px-4 py-2 rounded-lg text-sm font-semibold transition",
                        active
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
                      ].join(" ")}
                      aria-current={active ? "page" : undefined}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Arama + Ekle */}
            <div className="flex w-full sm:w-auto items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by account name..."
                  aria-label="Search accounts"
                  className="w-full sm:w-72 rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </div>

              <button
                onClick={() => go("/newaccount")}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 transition"
                title="Add an account"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        {/* Başlık + sayaç */}
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Accounts</h1>
            <p className="text-slate-500 text-sm">
              {debouncedSearch
                ? `Filtered results for “${debouncedSearch}”`
                : "All connected accounts"}
            </p>
          </div>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
            {isLoading ? "…" : `${filteredData.length}`} accounts
          </span>
        </div>

        {/* Hata durumu */}
        {err && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Yükleniyor iskeleti */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-slate-200 animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 w-3/5 bg-slate-200 rounded animate-pulse mb-2" />
                    <div className="h-3 w-2/5 bg-slate-200 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-9 mt-4 w-9 bg-slate-200 rounded-full self-end animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {/* Boş durum */}
        {!isLoading && !err && filteredData.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-slate-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-5.197-5.197M16.5 10.5a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z"
                />
              </svg>
            </div>
            <h3 className="font-semibold">Sonuç bulunamadı</h3>
            <p className="text-sm text-slate-500">
              Farklı bir anahtar kelime deneyebilir veya yeni bir hesap ekleyebilirsiniz.
            </p>
            <button
              onClick={() => go("/newaccount")}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200 transition"
            >
              Add Account
            </button>
          </div>
        )}

        {/* Grid liste */}
        {!isLoading && !err && filteredData.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredData.map((item) => (
              <article
                key={item._id}
                className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img
                      src={item.imageURL}
                      alt={item.name || "Account avatar"}
                      className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-100"
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='54%' font-size='16' font-family='Arial' text-anchor='middle' fill='%236b7280'>NA</text></svg>";
                      }}
                    />
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {item.name || "Unnamed"}
                    </h3>
                    <p className="text-xs text-slate-500 truncate mt-1">
                      {item.company || "—"}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* örnek rozet (varsa company gösterdik) */}
                    {item.company && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                        {item.company}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => openAccount(item._id)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200"
                    aria-label={`Open ${item.name || "account"}`}
                    title="Open details"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
